import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import WalletCashIcon from '@/assets/icons/wallet-cash';
import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useTopUp } from '@/feature/account-details-sweeps/hooks/use-top-up';
import { ClaimFunnelEvents } from '@/feature/analytics/sbk/claim-funnel-events';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { useClaimFunnelStore } from '@/hooks/use-claim-funnel-store';
import { Product } from '@/hooks/use-jurisdiction';
import { useProductSwitchModal } from '@/hooks/use-product-switch';
import { withOpacity } from '@/styles/styles';
import { toSocialCurrency } from '@/utils/numeric/currency';

import { useBetslipStore } from '../hooks/use-betslip-store';

/** Promo banner displayed in Picks Entry Confirmation that lets eligible users claim a free Social Sportsbook cash top-up. */
export const SweepsPromoBanner = () => {
    const { t } = useTranslation(['betslip_pickem', 'common']);
    const { data, isLoading } = useTopUp();
    const { changeProduct } = useProductSwitchModal();
    const setHasClaimedTopUpBonus = useBetslipStore(state => state.actions.setHasClaimedTopUpBonus);
    const { startClaim } = useClaimFunnelStore(state => state.actions);
    const { isProductAvailable } = useAvailableProducts();

    const isEligible = !isLoading && !!data && data.cash_top_up_amount > 0 && isProductAvailable(Product.Sweeps);

    const hasTrackedShown = useRef(false);
    useEffect(() => {
        if (isEligible && !hasTrackedShown.current) {
            hasTrackedShown.current = true;
            startClaim();
            ClaimFunnelEvents.ctaShown();
        }
    }, [isEligible, startClaim]);

    const onClaim = useCallback(() => {
        ClaimFunnelEvents.ctaTapped();
        setHasClaimedTopUpBonus(true);
        changeProduct(Product.Sweeps);
    }, [changeProduct, setHasClaimedTopUpBonus]);

    if (!isEligible) {
        return null;
    }

    return (
        <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            style={styles.banner}
            borderRadius="r20"
            paddingVertical="s16"
            paddingHorizontal="s16"
            marginTop="s32"
        >
            <Box flex={1} gap="s2">
                <Box flexDirection="row" alignItems="center" gap="s4">
                    <Text variant="titleMedium" color={ds.colorToken.text.primary}>
                        {t('betslip_pickem:sweeps_promo_title')}
                    </Text>
                    <WalletCashIcon size={16} />
                    <Text variant="titleMedium" color={ds.colorToken.text.primary}>
                        {toSocialCurrency(data.cash_top_up_amount)}!
                    </Text>
                </Box>
                <Text variant="bodySmall" color={ds.colorToken.text.primary} style={styles.subtitle}>
                    {t('betslip_pickem:sweeps_promo_subtitle')}
                </Text>
            </Box>
            <Button label={t('common:claim')} hierarchy="primary" size="m" shape="pill" onPress={onClaim} />
        </Box>
    );
};

const styles = StyleSheet.create({
    banner: {
        backgroundColor: withOpacity(ds.colorValue.background.brand.subtle, 0.4),
    },
    subtitle: {
        opacity: 0.7,
    },
});
